import React, { useEffect, useMemo, useState } from 'react';
import './DiscoverPeopleScreen.css';
import { useNavigate } from 'react-router-dom';
import api from '../api';

import backIcon from '../assets/backspace-icon.png';
import placeholderProfile from '../assets/placeholder-profile.png';

const GROUPS = [
  {
    key: 'suggestedForYou',
    title: 'Suggested for you',
    emptyText: 'No suggestions yet.',
  },
  {
    key: 'premiumPioneers',
    title: 'Premium Pioneers',
    emptyText: 'No premium pioneers to show yet.',
  },
  {
    key: 'badgeHolders',
    title: 'Badge holders',
    emptyText: 'No badge holders to show yet.',
  },
  {
    key: 'activeThisWeek',
    title: 'Active this week',
    emptyText: 'No active pioneers to show yet.',
  },
];

function DiscoverPeopleScreen({ user }) {
  const navigate = useNavigate();

  const [groups, setGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [followingMap, setFollowingMap] = useState({});
  const [busyMap, setBusyMap] = useState({});

  useEffect(() => {
    let cancelled = false;

    const fetchSuggestions = async () => {
      try {
        setLoading(true);

        const res = await api.get('/suggested-pioneers');
        const nextGroups = res.data?.groups || {};

        if (!cancelled) {
          setGroups(nextGroups);
        }
      } catch (err) {
        console.error('❌ Failed to fetch discover people:', err);

        if (!cancelled) {
          setGroups({});
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSuggestions();

    return () => {
      cancelled = true;
    };
  }, []);

  const formatFollowers = (count = 0) => {
    const n = Number(count) || 0;

    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M followers`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K followers`;
    if (n === 1) return '1 follower';

    return `${n} followers`;
  };

  const handleOpenProfile = (person) => {
    if (!person?.username) return;
    navigate(`/user/${person.username}`, { state: { from: 'discover-people' } });
  };

  const handleFollow = async (e, person) => {
    e.stopPropagation();

    if (!user?.username || !person?.username) return;

    const viewerUsername = String(user.username).trim().toLowerCase();
    const targetUsername = String(person.username).trim().toLowerCase();

    if (!viewerUsername || !targetUsername || viewerUsername === targetUsername) {
      return;
    }

    if (followingMap[targetUsername] || person.isFollowing) return;

    setBusyMap((prev) => ({
      ...prev,
      [targetUsername]: true,
    }));

    try {
      const res = await api.post('/suggested-pioneers/toggle-follow', {
        username: person.username,
      });

      const nowFollowing =
        typeof res.data?.nowFollowing === 'boolean'
          ? res.data.nowFollowing
          : true;

      setFollowingMap((prev) => ({
        ...prev,
        [targetUsername]: nowFollowing,
      }));

      if (nowFollowing) {
        setGroups((prev) => {
          const next = { ...prev };

          Object.keys(next).forEach((groupKey) => {
            const arr = Array.isArray(next[groupKey]) ? next[groupKey] : [];

            next[groupKey] = arr.map((item) => {
              const itemUsername = String(item?.username || '').trim().toLowerCase();

              if (itemUsername !== targetUsername) return item;

              return {
                ...item,
                isFollowing: true,
                followersCount:
                  typeof res.data?.followersCount === 'number'
                    ? res.data.followersCount
                    : Number(item.followersCount || 0) + 1,
              };
            });
          });

          return next;
        });
      }
    } catch (err) {
      console.error('❌ Discover people follow failed:', err);
    } finally {
      setBusyMap((prev) => ({
        ...prev,
        [targetUsername]: false,
      }));
    }
  };

  const visibleGroups = useMemo(() => {
    return GROUPS.map((group) => ({
      ...group,
      items: Array.isArray(groups[group.key]) ? groups[group.key] : [],
    }));
  }, [groups]);

  const hasAnyPeople = visibleGroups.some((group) => group.items.length > 0);

  return (
    <div className="discover-people-screen">
      <div className="discover-people-header">
        <button
          type="button"
          className="discover-people-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
        >
          <img src={backIcon} alt="Back" />
        </button>

        <h1>Discover people</h1>
      </div>

      <div className="discover-people-scroll">
        {loading ? (
          <div className="discover-people-loading-list">
            {Array.from({ length: 8 }).map((_, index) => (
              <div className="discover-person-row discover-person-row--skeleton" key={index}>
                <div className="discover-person-avatar-skeleton" />
                <div className="discover-person-info-skeleton">
                  <div className="discover-person-line-skeleton discover-person-line-skeleton--wide" />
                  <div className="discover-person-line-skeleton" />
                </div>
                <div className="discover-person-button-skeleton" />
              </div>
            ))}
          </div>
        ) : !hasAnyPeople ? (
          <div className="discover-people-empty">
            No suggested pioneers yet.
          </div>
        ) : (
          visibleGroups.map((group) => {
            if (!group.items.length) return null;

            return (
              <section className="discover-people-section" key={group.key}>
                <div className="discover-people-section-header">
                  <h2>{group.title}</h2>
                </div>

                <div className="discover-people-list">
                  {group.items.map((person) => {
                    const usernameKey = String(person?.username || '').trim().toLowerCase();

                    const isFollowing =
                      Boolean(person.isFollowing) || Boolean(followingMap[usernameKey]);

                    const isBusy = Boolean(busyMap[usernameKey]);

                    return (
                      <div
                        className="discover-person-row"
                        key={`${group.key}-${person._id || person.username}`}
                        onClick={() => handleOpenProfile(person)}
                      >
                        <img
                          src={person.profilePic || placeholderProfile}
                          alt={person.name || person.username || 'Pioneer'}
                          className="discover-person-avatar"
                          onError={(e) => {
                            e.currentTarget.src = placeholderProfile;
                          }}
                        />

                        <div className="discover-person-info">
                          <div className="discover-person-name">
                            {person.name || person.username}
                          </div>

                          <div className="discover-person-meta">
                            {formatFollowers(person.followersCount)}
                          </div>
                        </div>

                        <button
                          type="button"
                          className={`discover-person-follow-btn ${
                            isFollowing ? 'discover-person-follow-btn--following' : ''
                          }`}
                          disabled={isBusy || isFollowing}
                          onClick={(e) => handleFollow(e, person)}
                        >
                          {isBusy ? '...' : isFollowing ? 'Following' : 'Follow'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}

export default DiscoverPeopleScreen;
